import { useState, useRef, useEffect, useId } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * Inline help tooltip used on intake-form field labels.
 *
 * Renders an info trigger (outline info icon + optional visible label, styled
 * like the app's other links) that reveals a small help popover. Desktop opens
 * on hover/focus; touch devices open on tap. The popover closes on outside
 * tap/click or Escape.
 *
 * Visual language intentionally mirrors the existing Toast / modal components:
 * white surface, gray-200 border, rounded-lg, soft shadow, small gray text.
 */
export default function Tooltip({ label, children, className = '' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const tooltipId = useId();

  // Close when tapping/clicking outside (primarily for the touch/tap flow).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label ? undefined : 'More information'}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
      >
        <InformationCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        {label && <span>{label}</span>}
      </button>
      {open && (
        <span
          role="tooltip"
          id={tooltipId}
          className="absolute right-0 top-full z-20 mt-2 w-72 max-w-xs rounded-lg border border-gray-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-gray-600 shadow-lg"
        >
          {children}
        </span>
      )}
    </span>
  );
}
